import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, CheckSquare } from 'lucide-react';
import config from '../../configLoader';
import { EmployeeTask, Project } from '../../interfaces/EmployeeInterface';
import {
  PayrollEntryResult, PayrollTaskClaimBody, TASK_BUDGET_ERROR_MESSAGE,
} from '../../interfaces/TimesheetEditInterface';
import { ProjectTaskRow, ProjectTasksResponse } from '../../interfaces/ProjectTasksInterface';
import { money, parseAmt, ymdLabel } from './clockParse';
import { invalidateProjectTasks } from './lookups';
import { dupCounts, taskKey } from './taskPickerFilter';
import ProjectPicker, { PickedProject } from './ProjectPicker';
import TaskPicker, { TaskPickerMode } from './TaskPicker';
import { EntryErrorBanner, EntryModalShell, performEntrySave } from './EntryModalShell';
import { AmountEchoParts } from './echoParts';

/**
 * Task claim editor (M2-B, design shot 08 + the returned task-picker bundle).
 * Create: project → the TASK PICKER (tree or flat per tenant config) → amount,
 * which arrives prefilled with the whole remaining balance and selected (Q12).
 * Edit: the task is locked (§8.3 — amount is the ONLY editable field, D8).
 *
 * The budget error is a DESIGNED state: the API 400s with the exact message
 * "Insufficient remaining amount for this task." — the banner stays at the top
 * of the section, the summary's remaining figure turns red, the input keeps its
 * value and its `.bad` ring, and "Use remaining" sits under the field (Q9).
 */

export interface TaskClaimTarget {
  row: EmployeeTask;
  project: Project | null;
}

/**
 * What "Save + add another" carries into the next modal. Q6: carry restores
 * PLACE, not VALUE — the same lot opens with NOTHING selected, so a stray Enter
 * cannot re-save a duplicate claim.
 *
 * `taskID` alone cannot identify a dual-linked row (one task, two intermediate
 * links, two rows), so the path and the row key ride along. They are internal
 * to the editor — the page passes the object straight back.
 */
export interface TaskCarry {
  project: PickedProject | null;
  taskID: number | null;
  /** The claimed row's `intermediatePathIDs`, to re-expand its lot. */
  pathIDs?: number[] | null;
  /** The claimed row's `taskKey`, to mark it (never to select it). */
  rowKey?: string | null;
  /** The claimed row's breadcrumb, for the confirmation banner. */
  path?: string | null;
}

export interface TaskClaimModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: TaskClaimTarget | null;
  carry: TaskCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: TaskCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

/** Which of the three amount cases the typed value is in (drives chip + note). */
type AmountKind = 'blank' | 'full' | 'part' | 'over';

const TaskClaimModal: React.FC<TaskClaimModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  // Absent = 'tree' (WCDP, NPM drill down; 2020PH is the flat tenant).
  const pickerMode: TaskPickerMode = config.payrollTimesheetEditing?.taskPicker === 'flat'
    ? 'flat' : 'tree';

  const [project, setProject] = useState<PickedProject | null>(carry?.project ?? null);
  const [catalog, setCatalog] = useState<ProjectTasksResponse | null>(null);
  const [picked, setPicked] = useState<ProjectTaskRow | null>(null);
  const [amountRaw, setAmountRaw] = useState<string>(isEdit ? String(entry!.row.amountClaimed ?? '') : '');
  const [bad, setBad] = useState(false);
  const [budgetError, setBudgetError] = useState(false);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);

  // Q6 — a carry reopen shows the picker with the lot open and nothing picked.
  const [carryNote, setCarryNote] = useState<string | null>(carry?.path ?? null);

  /**
   * The picker stays mounted for the whole of add mode — picking a task adds
   * the summary and enables the amount above it, it does NOT swap the list
   * away. That is what keeps the card the same height through the whole
   * "Save + add another" loop, and it means the expansion state survives a
   * pick (the panel used to unmount and forget which lots were open).
   *
   * `pickerFocused` only drives the echo line's wording: the picker stops
   * propagation on every key it consumes, so the shell's Enter/Esc contract
   * already stands down on its own whenever a key is handled in the list.
   */
  const [pickerFocused, setPickerFocused] = useState(false);

  /** Bumped by `Change` to scroll the picked row back into view. */
  const [revealToken, setRevealToken] = useState(0);

  /** Which lot the picker should open on: the pick being changed, else the carry. */
  const expandPathIDs = useMemo(
    () => picked?.intermediatePathIDs ?? carry?.pathIDs ?? undefined,
    [picked, carry]
  );

  // A new project invalidates whatever was picked under the old one.
  useEffect(() => {
    if (isEdit) return;
    setPicked(null);
  }, [project, isEdit]);

  const onLoaded = useCallback((c: ProjectTasksResponse) => setCatalog(c), []);

  const onPick = useCallback((row: ProjectTaskRow) => {
    setPicked(row);
    setCarryNote(null);
    setBudgetError(false);
    setBad(false);
    // Q12 — full claim is the default: prefill the whole remaining balance and
    // SELECT it, so Enter saves the common case and typing replaces it.
    setAmountRaw(row.remainingAmount.toFixed(2));
    window.setTimeout(() => {
      amountRef.current?.focus();
      amountRef.current?.select();
    }, 0);
  }, []);

  // The budget headroom for "Use remaining": create → the task's remaining;
  // edit → remaining + this claim's current amount (the update moves the delta).
  const remainingInfo = useMemo(() => {
    if (isEdit) {
      const t = entry!.row.task;
      const remaining = t?.remainingAmount ?? 0;
      return {
        remaining,
        original: t?.originalAmount ?? 0,
        headroom: remaining + (entry!.row.amountClaimed ?? 0),
        altTaskID: t?.altTaskID ?? '',
      };
    }
    if (!picked) return null;
    return {
      remaining: picked.remainingAmount,
      original: picked.originalAmount,
      headroom: picked.remainingAmount,
      altTaskID: picked.altTaskID,
    };
  }, [isEdit, entry, picked]);

  // ── Q12: the amount chip + note ────────────────────────────────────────────
  const amountKind: AmountKind | null = useMemo(() => {
    if (isEdit || !picked) return null;
    const n = parseAmt(amountRaw);
    if (n == null) return 'blank';
    if (Math.abs(n - picked.remainingAmount) < 0.005) return 'full';
    return n > picked.remainingAmount ? 'over' : 'part';
  }, [isEdit, picked, amountRaw]);

  const claimFull = useCallback(() => {
    if (!picked) return;
    setAmountRaw(picked.remainingAmount.toFixed(2));
    setBudgetError(false);
    setBad(false);
    amountRef.current?.focus();
    amountRef.current?.select();
  }, [picked]);

  const amountChip = (() => {
    if (!amountKind || amountKind === 'blank') return null;
    if (amountKind === 'full') {
      return <span className="tp-amtchip full"><Check className="ic" />Full remaining</span>;
    }
    if (amountKind === 'over') {
      return <span className="tp-amtchip over"><AlertCircle className="ic" />Over remaining</span>;
    }
    return (
      <button className="tp-amtchip part" type="button" onClick={claimFull}>
        Partial · claim full instead
      </button>
    );
  })();

  const amountNote = (() => {
    if (!picked || isEdit || !amountKind || amountKind === 'over') return null;
    if (amountKind === 'blank') {
      return <span className="pe-note">Prefilled with the full remaining — clear it to claim part.</span>;
    }
    if (amountKind === 'full') {
      return (
        <span className="pe-note">
          Claims the whole piece. <b className="mono">{money(0)}</b> left after this — press{' '}
          <b>Enter</b> to save and go to the next piece.
        </span>
      );
    }
    const typed = parseAmt(amountRaw) ?? 0;
    return (
      <span className="pe-note">
        Partial claim — <b className="mono">{money(picked.remainingAmount - typed)}</b> stays
        open on {picked.altTaskID}.
      </span>
    );
  })();

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (close: boolean) => {
    const amount = parseAmt(amountRaw);
    if (amount == null || amount < 0) { setBad(true); amountRef.current?.focus(); return; }
    if (!isEdit && picked == null) {
      setBanner({ title: 'Pick a task first.', body: 'Choose the project and task this claim is against.' });
      return;
    }

    const body: PayrollTaskClaimBody = isEdit
      ? { amountClaimed: amount, editSessionID }
      : {
        employeeID, payPeriodID, taskID: picked!.taskID,
        amountClaimed: amount, entryDate: ymd, editSessionID,
      };

    setSaving(true);
    setBanner(null);
    setBudgetError(false);
    const outcome = await performEntrySave('task', isEdit ? entry!.row.employeeTaskID : null, body);
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) { onConflict(outcome.message); return; }
      if (outcome.message === TASK_BUDGET_ERROR_MESSAGE) {
        // Preserve what was typed; the banner states the remaining budget.
        setBudgetError(true);
        amountRef.current?.focus();
        return;
      }
      setBanner({ title: 'Could not save the task claim.', body: outcome.message });
      return;
    }
    // Every successful claim moves `remainingAmount` on the task — and on the
    // OTHER row of a dual-linked task too — so the cached catalog is now wrong.
    const savedProjectID = project?.projectID ?? entry?.project?.projectID ?? null;
    if (savedProjectID != null) invalidateProjectTasks(savedProjectID);
    onSaved(outcome.result, close, {
      project,
      taskID: picked?.taskID ?? null,
      pathIDs: picked?.intermediatePathIDs ?? null,
      rowKey: picked ? taskKey(picked) : null,
      path: picked ? (picked.intermediatePath || 'Direct tasks') : null,
    });
  };

  const useRemaining = () => {
    if (!remainingInfo) return;
    setAmountRaw(remainingInfo.headroom.toFixed(2));
    setBudgetError(false);
    amountRef.current?.focus();
  };

  const budgetBanner = budgetError && remainingInfo ? (
    <div className="pe-ban danger">
      <AlertCircle />
      <div className="grow">
        <b>Insufficient remaining amount for this task.</b>
        {remainingInfo.altTaskID} has <b>{money(remainingInfo.headroom)}</b> left
        of {money(remainingInfo.original)}. Lower the claim or pick another task — what you typed
        is still here.
      </div>
    </div>
  ) : null;

  const carryBanner = carryNote && !budgetBanner && !banner ? (
    <div className="pe-ban tp-carry">
      <CheckCircle2 />
      <div className="grow">
        <b>Saved. Next piece in {carryNote}.</b>
        Same lot is open and the piece you just claimed is marked — nothing is pre-selected,
        so Enter can&rsquo;t re-save it by accident.
      </div>
    </div>
  ) : null;

  // ── The selected summary (Q6 + Q9) ─────────────────────────────────────────
  const dupOf = picked && catalog ? (dupCounts(catalog.tasks)[picked.taskID] ?? 1) : 1;
  const selectedSummary = picked ? (
    <div className="tp-sel">
      <span className="mi"><CheckSquare className="ic" /></span>
      <div className="grow">
        <div className="l1">
          <span className="c">{picked.altTaskID}</span>
          <span>{picked.taskInfo?.description || picked.taskInfo?.name}</span>
        </div>
        <div className="l2">
          {picked.intermediatePath
            || (catalog?.hasDirectTasks ? 'Direct task — project root' : 'Unassigned — project root')}
          {' · '}acct {picked.account?.altAccountCodeID}
          {dupOf > 1 && ` · one budget shared with ${dupOf - 1} other location${dupOf > 2 ? 's' : ''}`}
        </div>
        <div className="l3">
          Remaining <b className={budgetError ? 'over' : undefined}>{money(picked.remainingAmount)}</b>
          {' '}of {money(picked.originalAmount)} · {picked.employeesClaimed?.length ?? 0} claimed
        </div>
      </div>
      <button
        className="tp-change"
        type="button"
        /* The list is right below, so Change no longer swaps a view back in —
           it scrolls the picked row into view and puts the cursor on it. */
        onClick={() => setRevealToken((n) => n + 1)}
      >
        Show in list
      </button>
    </div>
  ) : null;

  return (
    <EntryModalShell
      kind="task"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(ymd)} · ${employeeName}`}
      banner={budgetBanner ?? (banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null) ?? carryBanner}
      echo={isEdit || picked ? <AmountEchoParts raw={amountRaw} /> : null}
      /* Q7 — the echo line is the only place that can say WHICH Enter you are on. */
      keysHint={pickerFocused ? (
        <span>List has the keyboard — <span className="kbd">Enter</span> picks the highlighted task.</span>
      ) : undefined}
      /* Edit mode has no list to absorb slack, so it keeps the shipped
         auto-height; only add mode is pinned wide and tall. */
      modalClassName={isEdit ? undefined : 'tp-wide'}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      {isEdit ? (
        <div className="field">
          <label>Task</label>
          <div className="pe-fixed">
            <span className="mono">{entry!.row.task?.altTaskID}</span>
            {' '}· {entry!.row.task?.taskInfo?.name ?? ''}
            {entry!.row.intermediateName ? ` · ${entry!.row.intermediateName}` : ''}
          </div>
          {remainingInfo && (
            <span className="pe-fieldnote">
              Remaining <b>{money(remainingInfo.remaining)}</b> of {money(remainingInfo.original)} —
              this claim currently holds {money(entry!.row.amountClaimed ?? 0)}
            </span>
          )}
        </div>
      ) : (
        <>
          <div className="field">
            <label>Project</label>
            <ProjectPicker value={project} onChange={setProject} autoFocus placeholder="Search projects…" />
          </div>
          {/* The selected summary sits ABOVE the amount it describes; the list
              stays mounted below both (see `pickerFocused`). Q9 — the remaining
              figure lives here and nowhere else in add mode. */}
          {picked && (
            <div className="field">
              <label>Task</label>
              {selectedSummary}
            </div>
          )}
        </>
      )}
      <div className="row2">
        <div className="field">
          <div className="tp-fieldhead">
            <label>Amount claimed</label>
            {amountChip}
          </div>
          <input
            ref={amountRef}
            className={`pe-in${bad || budgetError ? ' bad' : ''}`}
            value={amountRaw}
            onChange={(ev) => { setAmountRaw(ev.target.value); setBad(false); setBudgetError(false); }}
            placeholder="0.00"
            autoFocus={isEdit}
            autoComplete="off"
            spellCheck={false}
            disabled={!isEdit && picked == null}
          />
          {!isEdit && picked == null
            ? <span className="pe-note">Pick a task first.</span>
            : amountNote}
        </div>
      </div>
      {budgetError && remainingInfo && (
        <button className="pe-linkbtn" onClick={useRemaining} type="button">
          Use remaining {money(remainingInfo.headroom)}
        </button>
      )}
      {/* The list is the LAST thing in the section and the only child that
          grows, so it absorbs whatever the summary, the amount note and any
          banner leave behind — which is what holds the card at one height
          through the whole add/save/add-again loop. `.tp-field` is the flex
          seam the stylesheet targets. */}
      {!isEdit && (
        <div
          className="field tp-field"
          onFocusCapture={() => setPickerFocused(true)}
          onBlurCapture={() => setPickerFocused(false)}
        >
          <div className="tp-fieldhead">
            <label>
              {picked ? 'Other tasks' : 'Task'}
              {project ? ` — ${pickerMode === 'tree' ? 'browse by intermediate' : 'all tasks on the project'}` : ''}
            </label>
            {project && (
              <span className="tp-hint">
                <span className="kbd">↑↓</span> move · <span className="kbd">→</span> open
                {' '}· <span className="kbd">⏎</span> pick
              </span>
            )}
          </div>
          <TaskPicker
            mode={pickerMode}
            projectID={project?.projectID ?? null}
            value={picked ? taskKey(picked) : null}
            onPick={onPick}
            onLoaded={onLoaded}
            expandPathIDs={expandPathIDs}
            justClaimedKey={carry?.rowKey ?? null}
            revealToken={revealToken}
          />
        </div>
      )}
    </EntryModalShell>
  );
};

export default TaskClaimModal;
